import { observer } from 'mobx-react';
import { useEffect, useState, useRef } from 'react';
import colors from 'assets/scss/colors.module.scss';
import Panel from 'app/components/Panel';
import { useStore } from 'app/context';
import { formatKw } from 'app/utils';
import { init, EChartsOption, ECharts } from 'echarts';
import { Icon } from 'app/components/common/Icon';

interface Props {
	agtId: number;
	onClose: () => void;
}

interface ChartDataPoint {
	kw: number;
	kwFormatted: string;
	quote: number;
	isAbove50: boolean;
}

export const AgtAkumaPlanerMultiGp26Detail = observer((props: Props) => {
	const { agtId, onClose } = props;
	const { agtAkumaPlanerStore, agtAkumaPlanerMultiGp26Store } = useStore();
	const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
	const [kwsAbove50Count, setKwsAbove50Count] = useState(0);
	const [maxKwFormatted, setMaxKwFormatted] = useState('');

	useEffect(() => {
		Promise.all([agtAkumaPlanerStore.loadWeeklyByAgt(agtId), agtAkumaPlanerMultiGp26Store.findAll()]).then(([weeklyItems, multiGpItems]) => {
			const dataByKw = new Map(weeklyItems.map((item) => [item.kw, item]));

			const points: ChartDataPoint[] = [];
			for (let kw = 202601; kw <= 202653; kw++) {
				const item = dataByKw.get(kw);
				const quote = item ? item.quote * 100 : 0;
				points.push({
					kw,
					kwFormatted: formatKw(kw),
					quote: Math.round(quote * 100) / 100,
					isAbove50: quote >= 50,
				});
			}

			const agtData = multiGpItems.find((m) => m.agtId === agtId);
			setKwsAbove50Count(agtData?.kwsAbove50Count ?? 0);

			const maxKw = weeklyItems.length > 0 ? Math.max(...weeklyItems.map((i) => i.kw)) : 0;
			setMaxKwFormatted(maxKw > 0 ? formatKw(maxKw) : '');
			setChartData(points);
		});
	}, [agtAkumaPlanerStore, agtAkumaPlanerMultiGp26Store, agtId]);

	// NEW: ECharts implementation
	const chartRefECharts = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!chartRefECharts.current || chartData.length === 0) return;

		const chart = init(chartRefECharts.current);

		// Prepare data for echarts
		const xAxisData = chartData.map((d) => d.kwFormatted);
		const quoteData = chartData.map((d) => d.quote);

		// Find the max KW index for reference line
		const maxKwIndex = maxKwFormatted ? chartData.findIndex((d) => d.kwFormatted === maxKwFormatted) : -1;

		// Build markLine data array
		const markLineData: any[] = [
			{
				yAxis: 50,
				lineStyle: {
					color: colors.orange,
					type: [6, 3] as any,
					width: 2,
				},
				label: {
					formatter: '50%',
					position: 'end',
					color: colors.orange,
				},
			},
		];

		// Add maxKw reference line if exists
		if (maxKwIndex >= 0) {
			markLineData.push({
				xAxis: maxKwIndex,
				lineStyle: {
					color: colors.black,
					type: [4, 2] as any,
					width: 2,
				},
				label: {
					show: true,
					formatter: () => {
						return `Letzter Datenstand: ${maxKwFormatted}`;
					},
					position: 'end',
					color: colors.blue,
					fontSize: 11,
					offset: [0, -15],
				},
			});
		}

		const option: EChartsOption = {
			animation: false,
			tooltip: {
				trigger: 'axis',
				formatter: (params: any) => {
					if (!Array.isArray(params) || params.length === 0) return '';
					const point = params[0];
					return `KW ${point.axisValue}<br/>${point.marker} Quote: ${point.value}%`;
				},
			},
			grid: {
				left: 60,
				right: 20,
				top: 50,
				bottom: 80,
				containLabel: false,
			},
			xAxis: {
				type: 'category',
				data: xAxisData,
				axisLabel: {
					interval: 3, // Show every 4th label
					rotate: -45,
					fontSize: 11,
				},
			},
			yAxis: {
				type: 'value',
				min: 0,
				max: 100,
				name: 'Quote %',
				nameLocation: 'middle',
				nameGap: 40,
				nameTextStyle: {
					fontSize: 12,
				},
				splitLine: {
					lineStyle: {
						type: 'dashed',
					},
				},
			},
			series: [
				{
					name: 'Quote',
					type: 'bar',
					data: quoteData.map((value, index) => ({
						value,
						itemStyle: {
							color: chartData[index].isAbove50 ? colors.green : colors.primary,
						},
					})),
					markLine: {
						silent: true,
						symbol: 'none',
						data: markLineData,
					},
				},
			],
		};

		chart.setOption(option);

		const handleResize = () => {
			chart.resize();
		};
		window.addEventListener('resize', handleResize);

		return () => {
			chart.dispose();
			window.removeEventListener('resize', handleResize);
		};
	}, [chartData, maxKwFormatted]);

	return (
		<Panel title="AKUMA Planer Übertragungsquote 2026" subtitle={`Agentur: ${agtId}`} onClose={onClose} header={<></>} classModifier="is-akumaplaner-detail">
			<div>
				<div className="pad-top-1rem content is-centered">
					<p>
						<strong>AKUMA Planer Übertragungsquote 2026</strong>
					</p>
				</div>
				{chartData.length > 0 ? <div ref={chartRefECharts} style={{ width: '100%', height: 350 }} /> : <div className="pad-1rem">Keine Daten vorhanden</div>}

				<div className="pad-1rem margin-top-3rem">
					<div className="note-box content margin-bottom-1rem">
						<p>
							<Icon iconClass="info-circle" />
						</p>
						<div className="note-message">
							<p>
								<strong>Hinweis</strong>
							</p>
							<ul>
								<li>
									Wochen mit Übertragungsquote {'>='} 50%: <strong>{kwsAbove50Count}</strong>
								</li>
								<li>GP Ziel: 12 Wochen mit Übertragungsquote {'>='} 50%</li>
							</ul>
							<p></p>
						</div>
					</div>
				</div>
			</div>
		</Panel>
	);
});
